/**
 * @engine/input — AxisControl
 *
 * 1D analog axis control with delta tracking.
 */

import type { ControlType, AxisState } from '../types';
import { InputControl } from './InputControl';

/**
 * Axis control — 1D analog value with delta tracking.
 * Typically represents triggers, scroll wheels, or individual stick axes.
 */
export class AxisControl extends InputControl<number> implements AxisState {
  readonly type: ControlType = 'axis';

  /**
   * Whether this is a full-range axis [-1..1] or half-range [0..1]
   */
  readonly isFullRange: boolean;

  constructor(name: string, isFullRange: boolean = true) {
    super(name, 0);
    this.isFullRange = isFullRange;
  }

  // ============================================================================
  // AxisState interface
  // ============================================================================

  get delta(): number {
    return this._value - this._previousValue;
  }

  // ============================================================================
  // Utilities
  // ============================================================================

  /**
   * Check if axis is past a threshold (treating as button)
   */
  isActive(threshold: number = 0.5): boolean {
    return Math.abs(this._value) >= threshold;
  }

  /**
   * Get direction (-1, 0, +1) based on value
   */
  get direction(): number {
    if (this._value > 0.1) return 1;
    if (this._value < -0.1) return -1;
    return 0;
  }

  /**
   * Clamp value to valid range
   */
  override update(newValue: number, time: number): void {
    const min = this.isFullRange ? -1 : 0;
    const clamped = Math.max(min, Math.min(1, newValue));
    super.update(clamped, time);
  }
}
